import React from 'react';

import { IconButton, Typography } from '@mui/material';
import { Copy, FilePlus2 } from 'lucide-react';
import rehypeRaw from 'rehype-raw';
import { AlineTooltip, Spacer } from 'src/components';
import { ContractIcon } from 'src/components/icons/ContractIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { makeStyles } from 'tss-react/mui';

import { reinstateHtmlElements } from './reinstateHtmlElements';
import { useAssistantMessageHandlers } from '../../../chat-hooks/useAssistantMessageHandlers';
import { AlineMarkdown } from '../../AlineMarkdown';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { remarkTable } from '../../remark-plugins';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  buttons: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.25),
  },
  header: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  iconWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  section: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2, 0),
    position: 'relative',
  },
}));

/**
 * Capture the frontmatter INCLUDING any trailing newlines.
 */
const frontMatterRegex = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

/**
 * Technically, Frontmatter is a YAML block; however, we use it for simple
 * key-value pairs separated by a colon. Since we only need to find one
 * key/value pair, we can use a simple regex to find the title.
 */
function extractTitleFromFrontMatter(frontMatter: string): string {
  const titleMatch = frontMatter.match(/^title:\s*(.*)$/m);
  return titleMatch?.[1] ?? '';
}

/**
 * Takes the entire code block and extracts the title (from FrontMatter) and
 * Markdown body. We do some light escaping of angle brackets in the Markdown
 * body to avoid rendering as HTML.
 */
function extractTitleAndBody(code: string): { markdown: string; title: string } {
  const frontMatterMatch = code.match(frontMatterRegex);
  if (!frontMatterMatch) return { markdown: '', title: '' };

  const frontMatter = frontMatterMatch[1] ?? '';
  const contentStartIndex = frontMatterMatch[0].length;
  const title = extractTitleFromFrontMatter(frontMatter);
  const markdown = escapeAngleBracketsInMarkdown(code.slice(contentStartIndex).trim());

  return { markdown, title };
}

/**
 * Note: Escaping is not strictly necessary for the Markdown renderer in the
 * browser (although it is not harmful); however, it IS necessary when creating
 * a new document from the Markdown as `<this>` will be interpreted as inline
 * HTML and will result in an empty string. It will also be helpful in copy to
 * clipboard depending on where we are pasting to.
 *
 * Also Note, we aren't escaping everything like "&" characters so that we don't
 * double scape in some instances. We are assuming that the AI is unreliable,
 * and may sometimes escape and sometimes not. If we see an angle bracket, we
 * can probably safely assume it was NOT meant to be HTML.
 */
function escapeAngleBracketsInMarkdown(text: string): string {
  return text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export const CodeDraftElement = React.memo(function CodeDraftElement({
  node,
}: MarkdownComponentProps) {
  const { classes } = useStyles();
  const { copyMarkdownToClipboard, createNewDocumentFromMarkdown } = useAssistantMessageHandlers();
  const codeBlockInfo = getCodeBlockInfoFromNode(node);

  if (!codeBlockInfo) return null;

  const { code, isNodeStreaming } = codeBlockInfo;
  const { markdown, title } = extractTitleAndBody(code);
  const disabledReason = isNodeStreaming ? 'AI is writing...' : '';
  const isDisabled = Boolean(disabledReason);
  const markdownWithBreaksReinstated = reinstateHtmlElements(markdown);

  return (
    <section className={classes.section}>
      <div className={classes.header}>
        <div className={classes.iconWrapper}>
          <ContractIcon />
        </div>
        <Typography variant="subtitle2">{title || 'Untitled'}</Typography>
        <Spacer />
        <div className={classes.buttons}>
          <AlineTooltip
            arrow
            placement="top"
            title={disabledReason || 'Copy AI draft to clipboard'}
          >
            <div>
              <IconButton
                disabled={isDisabled}
                size="small"
                onClick={() => {
                  copyMarkdownToClipboard(
                    markdownWithBreaksReinstated,
                    'AI draft copied to clipboard.'
                  );
                }}
              >
                <Copy size={16} stroke="currentColor" strokeWidth={1.5} />
              </IconButton>
            </div>
          </AlineTooltip>
          {/**
           * Legacy chat-agent (V1) drafter fences carry markdown + a
           * frontmatter title but no pre-allocated documentId, so the
           * harness's artifact-tracking path
           * (`recreateAgentArtifactDocument`) can't be reused here.
           * Match the table / AI-message Create-document affordance
           * instead — same client-side helper, same icon, same right-of-
           * copy position so the three surfaces feel like one feature.
           */}
          <AlineTooltip
            arrow
            placement="top"
            title={disabledReason || 'Create Aline document from draft'}
          >
            <div>
              <IconButton
                aria-label="Create document"
                disabled={isDisabled}
                size="small"
                onClick={() => {
                  void createNewDocumentFromMarkdown(
                    title || 'Untitled',
                    markdownWithBreaksReinstated
                  );
                }}
              >
                <FilePlus2 size={16} stroke="currentColor" strokeWidth={1.5} />
              </IconButton>
            </div>
          </AlineTooltip>
        </div>
      </div>
      <div className={classes.body}>
        <AlineMarkdown
          markdownString={markdownWithBreaksReinstated}
          rehypePlugins={[rehypeRaw]}
          remarkPlugins={[remarkTable]}
          type="draft-document"
        />
      </div>
    </section>
  );
});

const useAiWritingStyles = makeStyles()(theme => ({
  message: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: theme.appBorders.outline,
    borderRadius: 16,
    bottom: 36,
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'center',
    left: '50%',
    padding: theme.spacing(0.5, 1),
    position: 'absolute',
    transform: 'translateX(-50%)',
    width: 'fit-content',
  },
}));

export function AIWritingMessage({
  isWriting,
  msg = 'AI is writing...',
}: {
  isWriting: boolean;
  msg?: string;
}): JSX.Element | null {
  const { classes } = useAiWritingStyles();

  if (!isWriting) return null;

  return (
    <div className={classes.message}>
      <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
      <Typography color="textSecondary">{msg}</Typography>
    </div>
  );
}
